import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import type { BaseQueryApi, BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { baseQueryWithReauth } from "@/shared/api";

export const COMMUNITY_UNAVAILABLE = "community_unavailable";

const TOKEN_URL = "/api/community/token/";
const API_PREFIX = "/api/v1";
const REFRESH_MARGIN_MS = 60_000;

interface CommunityPass {
  token: string;
  baseUrl: string;
  expiresAt: number;
}

interface PassResponse {
  token: string;
  expires_in: number;
  community_url: string;
}

type PassResult = { pass: CommunityPass } | { error: FetchBaseQueryError };

type ExtraOptions = Parameters<BaseQueryFn>[2];

let currentPass: CommunityPass | null = null;
let pendingPass: Promise<PassResult> | null = null;
let sessionId = 0;

export function resetCommunityPass(): void {
  currentPass = null;
  pendingPass = null;
  sessionId += 1;
}

function buildUnavailableError(cause?: FetchBaseQueryError): FetchBaseQueryError {
  return { status: "CUSTOM_ERROR", error: COMMUNITY_UNAVAILABLE, data: { cause: cause?.status ?? null } };
}

function readServiceOrigin(value: string): string | null {
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    return value.replace(/\/+$/, "");
  } catch {
    return null;
  }
}

function isPassResponse(data: unknown): data is PassResponse {
  if (typeof data !== "object" || data === null) return false;
  const candidate = data as Partial<PassResponse>;
  return (
    typeof candidate.token === "string" &&
    candidate.token.length > 0 &&
    typeof candidate.expires_in === "number" &&
    candidate.expires_in > 0 &&
    typeof candidate.community_url === "string"
  );
}

async function requestPass(api: BaseQueryApi, extraOptions: ExtraOptions): Promise<PassResult> {
  const detachedApi: BaseQueryApi = { ...api, signal: new AbortController().signal };
  try {
    const result = await baseQueryWithReauth({ url: TOKEN_URL, method: "GET" }, detachedApi, extraOptions);
    if (result.error) return { error: buildUnavailableError(result.error) };
    if (!isPassResponse(result.data)) return { error: buildUnavailableError() };

    const baseUrl = readServiceOrigin(result.data.community_url);
    if (!baseUrl) return { error: buildUnavailableError() };

    return {
      pass: {
        token: result.data.token,
        baseUrl,
        expiresAt: Date.now() + result.data.expires_in * 1000,
      },
    };
  } catch {
    return { error: buildUnavailableError() };
  }
}

function isFresh(pass: CommunityPass | null): pass is CommunityPass {
  return pass !== null && pass.expiresAt - Date.now() > REFRESH_MARGIN_MS;
}

async function obtainPass(api: BaseQueryApi, extraOptions: ExtraOptions): Promise<PassResult> {
  if (isFresh(currentPass)) return { pass: currentPass };

  if (!pendingPass) {
    const startedIn = sessionId;
    const request: Promise<PassResult> = requestPass(api, extraOptions).then(function storePass(result) {
      if (pendingPass === request) pendingPass = null;
      if (startedIn !== sessionId) return { error: buildUnavailableError() };
      if ("pass" in result) currentPass = result.pass;
      return result;
    });
    pendingPass = request;
  }

  return pendingPass;
}

function discardPass(pass: CommunityPass): void {
  if (currentPass === pass) currentPass = null;
}

function callService(pass: CommunityPass, args: string | FetchArgs, api: BaseQueryApi, extraOptions: ExtraOptions) {
  const serviceQuery = fetchBaseQuery({
    baseUrl: `${pass.baseUrl}${API_PREFIX}`,
    credentials: "omit",
    prepareHeaders(headers) {
      headers.set("Authorization", `Bearer ${pass.token}`);
      return headers;
    },
  });
  return serviceQuery(args, api, extraOptions ?? {});
}

export const communityBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async function (
  args,
  api,
  extraOptions,
) {
  const first = await obtainPass(api, extraOptions);
  if ("error" in first) return { error: first.error };

  const result = await callService(first.pass, args, api, extraOptions);
  if (result.error?.status !== 401) return result;

  discardPass(first.pass);
  const second = await obtainPass(api, extraOptions);
  if ("error" in second) return { error: second.error };

  return callService(second.pass, args, api, extraOptions);
};
